import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderOpen, Settings, Trash2, PaintBucket, Copy, Home, DoorOpen, TreeDeciduous, LogOut, Shield, LogIn, HardDriveDownload, FilePlus, ListChecks, Calculator, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { projectsApi } from "@/lib/api";
import { getBrowserEstimates, removeBrowserEstimate, downloadBrowserEstimate } from "@/lib/browserStore";
import { formatDate, formatCurrency, PROJECT_TYPES } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newProject, setNewProject] = useState({
    name: "", client_name: "", client_address: "", client_phone: "", client_email: "", project_type: "interior"
  });
  const [browserSaves, setBrowserSaves] = useState([]);

  useEffect(() => { loadProjects(); setBrowserSaves(getBrowserEstimates()); }, []);

  const handleRemoveBrowserSave = (projectId) => {
    setBrowserSaves(removeBrowserEstimate(projectId));
    toast.success("Removed from browser");
  };

  const loadProjects = async () => {
    try { const response = await projectsApi.getAll(); setProjects(response.data); } 
    catch (error) { toast.error("Failed to load projects"); } 
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newProject.name.trim()) { toast.error("Project name is required"); return; }
    try {
      const response = await projectsApi.create(newProject);
      setProjects([response.data, ...projects]);
      setCreateOpen(false);
      setNewProject({ name: "", client_name: "", client_address: "", client_phone: "", client_email: "", project_type: "interior" });
      toast.success("Project created");
      navigate(`/project/${response.data.id}`);
    } catch (error) { toast.error("Failed"); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await projectsApi.delete(deleteId); setProjects(projects.filter(p => p.id !== deleteId)); toast.success("Deleted"); } 
    catch (error) { toast.error("Failed"); } 
    finally { setDeleteId(null); }
  };

  const handleClone = async (id, e) => {
    e.stopPropagation();
    try { const response = await projectsApi.clone(id); setProjects([response.data, ...projects]); toast.success("Cloned"); } 
    catch (error) { toast.error("Failed"); }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getProjectIcon = (type) => {
    if (type === "deck") return <TreeDeciduous className="w-4 h-4" />;
    if (type === "exterior") return <Home className="w-4 h-4" />;
    if (type === "all") return <><DoorOpen className="w-4 h-4" /><Home className="w-4 h-4" /><TreeDeciduous className="w-4 h-4" /></>;
    if (type === "both") return <><DoorOpen className="w-4 h-4" /><Home className="w-4 h-4" /></>;
    return <DoorOpen className="w-4 h-4" />;
  };

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center"><PaintBucket className="w-5 h-5 text-black" /></div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">PAINT ESTIMATOR</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="flex items-center gap-2 mr-2 px-3 py-1 bg-secondary/50 rounded-sm">
                  {user.role === "admin" && <Shield className="w-4 h-4 text-amber-500" />}
                  <span className="text-sm">{user.name || user.email}</span>
                </div>
                <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-btn"><Settings className="w-4 h-4 mr-2" />Settings</Button>
                <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-5 h-5" /></Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-btn"><Settings className="w-4 h-4 mr-2" />Settings</Button>
                <Button variant="outline" onClick={() => navigate("/login")} data-testid="signin-btn">
                  <LogIn className="w-4 h-4 mr-2" />Sign In
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-12">
          <h3 className="font-heading text-xl font-bold tracking-tight mb-1">How This Works</h3>
          <p className="text-muted-foreground text-sm mb-4">A project goes from blank to a client-ready proposal in four steps.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <FilePlus className="w-6 h-6 text-amber-500 mb-2" />
                <p className="font-heading font-semibold text-sm mb-1">1. Create a Project</p>
                <p className="text-xs text-muted-foreground">Pick a type - Interior, Exterior, Deck, Cabinet Refinishing, or a full-service combo.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <ListChecks className="w-6 h-6 text-amber-500 mb-2" />
                <p className="font-heading font-semibold text-sm mb-1">2. Add Rooms &amp; Areas</p>
                <p className="text-xs text-muted-foreground">Every room starts blank - toggle on only the items this specific job actually needs.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <Calculator className="w-6 h-6 text-amber-500 mb-2" />
                <p className="font-heading font-semibold text-sm mb-1">3. Watch the Estimate Update</p>
                <p className="text-xs text-muted-foreground">Labor, materials, overhead, and profit recalculate live as you toggle items.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <FileText className="w-6 h-6 text-amber-500 mb-2" />
                <p className="font-heading font-semibold text-sm mb-1">4. Generate the Proposal</p>
                <p className="text-xs text-muted-foreground">One click makes a client-ready PDF/Word Proposal and Agreement - lump sum only, no line-item pricing shown.</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Production rates, material prices, overhead/profit, and payment terms are all yours to tune in{" "}
            <button onClick={() => navigate("/settings")} className="text-amber-500 hover:underline font-medium">Settings</button>.
          </p>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Projects</h2>
            <p className="text-muted-foreground mt-1">Manage paint estimates</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />New Project</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading text-xl">Create New Project</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="input-group"><Label className="input-label">Project Name *</Label><Input value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="e.g., Johnson Residence" /></div>
                <div className="input-group">
                  <Label className="input-label">Project Type</Label>
                  <Select value={newProject.project_type} onValueChange={(v) => setNewProject({ ...newProject, project_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="input-group"><Label className="input-label">Client Name</Label><Input value={newProject.client_name} onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })} /></div>
                <div className="input-group"><Label className="input-label">Client Address</Label><Input value={newProject.client_address} onChange={(e) => setNewProject({ ...newProject, client_address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Phone</Label><Input value={newProject.client_phone} onChange={(e) => setNewProject({ ...newProject, client_phone: e.target.value })} /></div>
                  <div className="input-group"><Label className="input-label">Email</Label><Input value={newProject.client_email} onChange={(e) => setNewProject({ ...newProject, client_email: e.target.value })} /></div>
                </div>
                <Button onClick={handleCreate} className="btn-primary w-full">Create Project</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (<Card key={i} className="animate-pulse"><CardHeader><div className="h-6 bg-muted rounded w-2/3"></div></CardHeader><CardContent><div className="h-4 bg-muted rounded w-1/2 mb-2"></div></CardContent></Card>))}
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <FolderOpen className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">No Projects Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first paint estimate</p>
            <Button onClick={() => setCreateOpen(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Create First Project</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="card-hover cursor-pointer group" onClick={() => navigate(`/project/${project.id}`)}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <CardTitle className="font-heading text-lg font-semibold tracking-tight">{project.name}</CardTitle>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handleClone(project.id, e)}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setDeleteId(project.id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.client_name && <p className="text-sm text-muted-foreground mb-1">{project.client_name}</p>}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {getProjectIcon(project.project_type)}
                      <span>{project.rooms?.length || 0} rooms</span>
                      {["exterior", "both", "all"].includes(project.project_type) && <span>• {project.exterior_sides?.length || 0} sides</span>}
                      {["deck", "all"].includes(project.project_type) && <span>• {project.deck_areas?.length || 0} decks</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(project.updated_at || project.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {browserSaves.length > 0 && (
          <div className="mt-12" data-testid="browser-saves-section">
            <div className="flex items-center gap-2 mb-4">
              <HardDriveDownload className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading text-xl font-bold tracking-tight">Saved in this browser</h3>
            </div>
            <p className="text-muted-foreground text-sm mb-4">Local snapshots kept on this device. Download a backup or remove them anytime.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {browserSaves.map((s) => (
                <Card key={s.projectId} className="border-amber-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg font-semibold tracking-tight">{s.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {s.clientName && <p className="text-sm text-muted-foreground mb-1">{s.clientName}</p>}
                    {s.totalCost != null && <p className="font-mono text-amber-500 mb-2">{formatCurrency(s.totalCost)}</p>}
                    <p className="text-xs text-muted-foreground mb-3">Saved {formatDate(s.savedAt)}</p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadBrowserEstimate(s)} data-testid={`browser-save-download-${s.projectId}`}>
                        <FolderOpen className="w-4 h-4 mr-2" />Download Backup
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoveBrowserSave(s.projectId)} data-testid={`browser-save-remove-${s.projectId}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Project?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
